import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  checkPlaceholders,
  extractEmailFromAddressText,
  formatDatetime,
  highlightPlaceholder,
  removeClassesFromHtml,
  sanitizeHtml,
} from "@_utils/helpers";
import { caseActions } from "@_actions/case.actions";
import { casesActions } from "@_actions/cases.actions";
import DialogPopup from "@_components/DialogPopup/DialogPopup";
import ErrorMessagePopup from "../ErrorMessagePopup/ErrorMessagePopup";
import Button from "@mui/material/Button";
import ErrorIcon from "@mui/icons-material/Error";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import styles from "./RenderPopup.module.css";
import { catchError } from "@_utils/helpers";
import CircularProgress from "@mui/material/CircularProgress";
import {
  Checkbox,
  FormControlLabel,
  Menu,
  MenuItem,
  RadioGroup,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DateInput from "@_components/DateInput/DateInput";
import TextfieldInputSmall from "@_components/TextfieldInput/TextfieldInputSmall";
import { caseActivityActions } from "@_actions/caseActivity.actions";
import AreYouSurePopup from "@_components/popups/AreYouSurePopup/AreYouSurePopup";

import RichTextEditor from "@_components/RichTextEditor/RichTextEditor";
import { caseConstants } from "@_constants/case.constants";
import { emailConstants } from "@_constants/email_flows.constants";
import { invoiceTemplateConstants } from "@_constants/invoiceTemplate.constants";
import AddressComponent from "./AddressComponent/AddressComponent";
import Scrollbar from "react-perfect-scrollbar-z";
import { invoiceTemplateService } from "@_services/invoiceTemplate.service";
import InfoPopup from "../InfoPopup/InfoPopup";
import { customerActions } from "@_actions/customer.actions";
import AppSnackbar from "@_components/AppSnackbar/AppSnackbar";
import { useSnackbar } from "@_hooks/useSnackbar";

const defaultState = {
  to: "",
  subject: "",
  content: "",
  // bcc: '',
};

/*
  --- HIGH PRIORITY ISSUES TO FIX ---

  1. Overlapping useEffect hooks:
      Three useEffect hooks (reply init, non-reply with signature, non-reply again) 
      all touch the editor and state for non-reply flow.
      These can overlap and fight each other when props.open or data changes, 
      leading to race conditions or unexpected state.
      Fix: One effect manages all editor/content initialization.
        Clear conditional logic for reply / non-reply.
        No race conditions or state overwrites from multiple effects.
        Easier to maintain and reason about.

  2. setTimeout usage is incorrect:
      setTimeout(() => dispatch(casesActions.getCases(), 500));
      // The 500 is passed to dispatch, not setTimeout. Should be:
      // setTimeout(() => dispatch(casesActions.getCases()), 500);

  3. Accessing caseReducer.case[caseId] without null checks:
      If caseReducer.case or caseReducer.case[caseId] is missing (slow load, wrong id, race), this throws 
      and the whole popup crashes.
      Add checks: const caseData = caseReducer.case ? caseReducer.case[caseId] : null;

  4. Typo in error message:
      "Failed to download Invoice for attachment." should be "download".

  5. Inefficient array filtering in addChoosenInvoicesToMail:
      Consider building a map for faster lookups.

  6. Large component:
      Consider splitting into smaller components for maintainability.
      
  --- END HIGH PRIORITY ISSUES ---
*/

function RenderPopup(props) {
  const {
    data,
    caseId,
    emailSetup,
    activity,
    resumeAutomation,
    email,
    replyToAll,
    JwtData,
    isReplyMode,
    title,
    invoice,
  } = props;
  var editorInstance = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const isClosingRef = useRef(false);
  const closePopup = props.close;
  const [attachments, setAttachments] = useState([]);

  const caseReducer = useSelector((state) => state.caseReducer);
  const customerReducer = useSelector((state) => state.customerReducer);
  /* issue: If caseReducer.case[caseId] is missing (slow load, wrong id, race), 
  this throws and the whole popup crashes.*/
  const opponentContactDetails =
    caseReducer.case[caseId].opponent_contact_details;
  const userReducer = useSelector((state) => state.userReducer);
  const caseActivityReducer = useSelector((state) => {
    return state.caseActivityReducer;
  });
  const autoHideDuration = 3000;
  const { snackbarProps, showSnackbarSuccess, resetSnackbar } = useSnackbar({
    autoHideDuration,
  });
  const contentRef = useRef(null);
  const dispatch = useDispatch();
  // Check for the missing placeholder on the first data loading
  const [missingPlaceholder, setMissingPlaceholder] = React.useState(false);
  // Update the content of text Editor change
  const [editedText, setEditedText] = React.useState();
  // Display send component or copy to clip base on the user server setup
  const [sendEmailTag, setSendEmailTag] = React.useState(true);
  const [inputs, setInputs] = useState(defaultState);

  // Snackbar and error message popup
  const [errorSnackbarTitle, setErrorSnackbarTitle] = useState("");
  const [errorSnackbar, setErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  // ReminderMenu
  const [reminderMenuOpen, setReminderMenuOpen] = useState(null);
  const [reminderSetting, setReminderSetting] = useState("in 7 days");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [reminderDate, setReminderDate] = useState(null);
  const [doSaveDeadline, setDoSaveDeadline] = useState(
    isReplyMode ? false : true,
  );
  const [reminderName, setReminderName] = useState("Follow Up: ");
  const [showSaveReminderPopup, setShowSaveReminderPopup] = useState(false);
  const [showAreYouSurePopup, setShowAreYouSurePopup] = useState(false);
  const [deleteReminder, setDeleteReminder] = useState(false);

  // invoice attachments
  const [chooseInvoicePopupOpen, setChooseInvoicePopupOpen] = useState(false);
  const [noInvoicesPopupOpen, setNoInvoicesPopupOpen] = useState(false);
  const [choosenInvoices, setChoosenInvoices] = useState([]);
  const [didPreviewLetter, setDidPreviewLetter] = useState(false);
  const [hasUnreviewedChanges, setHasUnreviewedChanges] = useState(false);
  const [openLetterCostPopup, setOpenLetterCostPopup] = useState(false);

  // Calculate minDate for reminder date picker (tomorrow)
  const reminderMinDate = React.useMemo(() => {
    return moment().add(1, "days");
  }, []);

  /**
   * Key of autocomplete --> needed to rerender the component after accepting a email
   */
  useEffect(() => {
    if (isReplyMode || data.name === emailConstants.NEW_MAIL)
      setReminderName("Follow up");
    else setReminderName("Follow Up: " + data.name);
  }, [data.name, isReplyMode]);
  // save copy paste email to log dialog
  const [open, setOpen] = useState(false);

  const handleOpenPopup = () => {
    setOpen(true);
    copyTextToClipboard(contentRef.current);
  };

  const handleClosePopup = () => {
    setDidPreviewLetter(false);
    setOpen(false);
  };

  const handleOpenReminderMenu = (event) => {
    setReminderMenuOpen(event.currentTarget);
  };

  const handleCloseReminderMenu = () => {
    setReminderMenuOpen(null);
  };

  useEffect(() => {
    if (invoice && props.open && attachments.length === 0) {
      dowloadAndAddInvoice(invoice);
    }
    // eslint-disable-next-line
  }, [invoice, props.open]);

  const handleChangeReminderOption = (numberOrCustom) => {
    if (typeof numberOrCustom === "number") {
      setReminderSetting("in " + numberOrCustom + " days");
      handleCloseReminderMenu();
      setShowDatePicker(false);
    } else {
      // activate datepicker
      setReminderSetting("on");
      handleCloseReminderMenu();
      setShowDatePicker(true);
    }
  };

  const setTextToContent = (content, goToFirstLine) => {
    // Don't update editor if popup is closing
    if (isClosingRef.current || !content) return;
    const cleanContent = sanitizeHtml(content);
    cleanContent && setMissingPlaceholder(checkPlaceholders(cleanContent));
    const newContent = cleanContent && highlightPlaceholder(cleanContent);
    if (editorInstance.current && editorInstance.current.setContents) {
      editorInstance.current.setContents("");
      editorInstance.current.insertHtmlNoNewLine(newContent, goToFirstLine);
    }
    setEditedText(newContent);
  };

  /**
   * Initial load of data --> editor content is set as well as subject and to field.
   * Only called once at the beginning.
   */
  useEffect(() => {
    // Skip when popup closes — this effect is for initialization only,
    // otherwise it resets the success snackbar before the user can see it
    if (!props.open) return;
    if (isReplyMode && email) {
      if (loading === true) return;
      // This is necessary because of the current popup implementation
      setErrorSnackbar(false);

      if (!emailSetup) {
        setSendEmailTag(false);
      } else {
        setSendEmailTag(true);
      }

      // get email signature
      var emailSignature = "";
      if (
        userReducer.user_signature &&
        typeof userReducer.user_signature === "string" &&
        userReducer.user_signature.length > 0 &&
        data?.type !== emailConstants.TEMPLATE_TYPE_LETTER
      )
        emailSignature = userReducer.user_signature;

      /**
       * We extend the mail with the reply header
       */
      var html = `<span><br></span><span><br></span>
    <div> ${emailSignature} </div> 
    <span><br></span>
    <hr style="display:inline-block;width:98%;color:black" tabindex="-1"></hr>
    <div id="divRplyFwdMsg" dir="ltr">
      <font face="Calibri, sans-serif" style="font-size:11pt" color="#000000">
      <b>From:</b> ${
        email.from && email.from.length > 0 ? email.from[0].address : ""
      }<br>
      <b>Sent:</b>  ${
        props.isSelfReply
          ? formatDatetime(email.created)
          : formatDatetime(email.data.date)
      }<br>
      <b>To:</b> ${
        email.to && email.to.length > 0 ? email.to[0].address : ""
      }<br>
      <b>Subject:</b> ${email.data.subject}</font>
      <div>&nbsp;</div></div>
      ${
        typeof email.data.html === "boolean"
          ? email.data.textAsHtml
          : "<div>" + email.data.html + "</div>"
      }
    `;

      // update the editor
      if (data.content) {
        setTextToContent(
          "<div>" +
            (data.content !== null &&
            data.content !== "" &&
            data.content?.length > 0
              ? data.content
              : "<p>&nbsp;</ br></p>") +
            "</div>" +
            html,
          true,
        );
      } else setContent(html);

      let emails = "";
      let ccMail = "";

      // find target email source
      let source = "from";
      if (props.isSelfReply === true) source = "to";

      // is reply all active?
      if (replyToAll === true && source === "from") {
        emails = extractEmailFromAddressText(
          email.from && email.from.length
            ? email.from[0].address !== JwtData.smtp_email
              ? email.from[0].address
              : ""
            : "",
        );
        // clean with refactoring add cc and to as one list
        if (email.to && email.to.length) {
          email.to.forEach((email) => {
            if (email.address !== JwtData.smtp_email) {
              emails =
                emails.length > 0
                  ? emails + ";" + extractEmailFromAddressText(email.address)
                  : extractEmailFromAddressText(email.address);
            }
          });
        }
        if (email.data.cc && email.data.cc.value.length) {
          email.data.cc.value.forEach((email) => {
            if (email.address !== JwtData.smtp_email) {
              ccMail =
                ccMail.length > 0
                  ? ccMail + ";" + extractEmailFromAddressText(email.address)
                  : extractEmailFromAddressText(email.address);
            }
          });
        }
      }

      // update inner state accordingly
      setInputs({
        to: extractEmailFromAddressText(
          email[source] && email[source].length
            ? // email could be email itself or in an array
              Array.isArray(email[source])
              ? email[source][0].address
              : email[source]
            : "",
        ),
        subject: "Re: " + email.data.subject,
        cc: replyToAll && ccMail ? ccMail : "",
        content: html, // TODO: format answer
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReplyMode, props.open, editorInstance.current]);

  /*
  why we have 2 seprate useEffects for !isReplyMode? wouldn't above useEffect and following 2 useEffects cause overlap?
  */
  // this will update the editor when its loaded to initial value
  useEffect(() => {
    // Guard: only run after editor is initialized for this open
    if (!props.open || !editorReady) {
      return;
    }
    if (
      !isReplyMode &&
      editorInstance.current &&
      editorInstance.current.insertHtmlNoNewLine
    ) {
      setDidPreviewLetter(false);
      var emailSignature = "";
      if (
        userReducer.user_signature &&
        typeof userReducer.user_signature === "string" &&
        userReducer.user_signature.length > 0 &&
        data?.type !== emailConstants.TEMPLATE_TYPE_LETTER
      ) {
        emailSignature = userReducer.user_signature;
        setTextToContent(
          "<div>" +
            (data.content !== null &&
            data.content !== "" &&
            data.content?.length > 0
              ? data.content
              : "<p>&nbsp;</ br></p>") +
            "</div>" +
            "<div>" +
            emailSignature +
            "</div>",
          true,
        );
      } else {
        setTextToContent(data.content ?? "<p>&nbsp;</ br></p>", true);
      }
    }
    // this effect must be executed when editor instance is set.
    // Or when the popup gets opened/closed
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorReady, data.content, props.open, isReplyMode]);

  useEffect(() => {
    if (!isReplyMode) {
      // This is necessary because of the current popup implementation
      setErrorSnackbar(false);
      setDidPreviewLetter(false);

      if (!emailSetup) {
        setSendEmailTag(false);
      } else {
        setSendEmailTag(true);
      }
      setTextToContent(data.content, true);
      setInputs({
        to: data.opponentEmail,
        subject: data.subject,
        content: data.content,
      });
    }
  }, [data.content, data.opponentEmail, data.subject, emailSetup, isReplyMode]);

  const setContent = (data) => {
    setMissingPlaceholder(checkPlaceholders(data));
    setTextToContent(data);
    setDidPreviewLetter(false);
  };

  function copyTextToClipboard(element) {
    function listener(e) {
      e.clipboardData.setData(
        "text/html",
        removeClassesFromHtml(editorInstance.current.getHtmlUnleaned()),
      );
      e.preventDefault();
    }

    document.addEventListener("copy", listener);
    document.execCommand("copy");
    document.removeEventListener("copy", listener);

    var sel, range;
    if (window.getSelection && document.createRange) {
      sel = window.getSelection();
      if (sel.toString() === "") {
        window.setTimeout(function () {
          range = document.createRange();
          range.selectNodeContents(element);
          sel.removeAllRanges();
          sel.addRange(range);
        }, 1);
      }
    } else if (document.selection) {
      sel = document.selection.createRange();
      if (sel.text === "") {
        range = document.body.createTextRange();
        range.moveToElementText(element);
        range.select();
      }
    }
  }

  function handleInputChange(e) {
    const { name, value } = e.target;
    setInputs({ ...inputs, [name]: value });
  }
  const setReminderChecked = (event) => {
    setDoSaveDeadline(Boolean(event.target.checked));
    if (caseReducer.case[caseId].reminder_date) {
      setShowAreYouSurePopup(!Boolean(event.target.checked));
    }
  };

  const handleDeleteReminder = () => {
    setDeleteReminder(true);
    setShowAreYouSurePopup(false);
  };

  const handleAreYouSurePopupClose = () => {
    setShowAreYouSurePopup(false);
  };

  function saveDeadline(doOpenEmailLogPopup) {
    // fetch infos
    let dateInput = new Date();
    if (doSaveDeadline) {
      if (reminderSetting === "custom") {
        dateInput = reminderDate;
      } else {
        switch (reminderSetting) {
          case "in 3 days":
            dateInput.setDate(dateInput.getDate() + 3);
            break;
          case "in 5 days":
            dateInput.setDate(dateInput.getDate() + 5);
            break;
          case "in 7 days":
            dateInput.setDate(dateInput.getDate() + 7);
            break;
          case "in 14 days":
            dateInput.setDate(dateInput.getDate() + 14);
            break;
          case "in 30 days":
            dateInput.setDate(dateInput.getDate() + 30);
            break;
          default:
            break;
        }
      }
      dispatch(
        caseActions.updateReminder(caseId, {
          name: reminderName,
          date: dateInput,
        }),
      );
    } else if (deleteReminder) {
      dispatch(caseActions.updateReminder(caseId));
      setDeleteReminder(false);
    }
    setShowSaveReminderPopup(false);
    setTimeout(() => dispatch(casesActions.getCases()), 500);
    if (doOpenEmailLogPopup) handleOpenPopup();
    return true;
  }

  const sendEmail = (e) => {
    e.preventDefault();

    const updatedInputs = {
      ...inputs,
      content: removeClassesFromHtml(editorInstance.current.getHtmlUnleaned()),
    };
    setInputs(updatedInputs);

    if (
      !missingPlaceholder &&
      updatedInputs.to &&
      updatedInputs.subject &&
      updatedInputs.content
    ) {
      if (!resumeAutomation && saveDeadline()) {
        setTextToContent(
          removeClassesFromHtml(editorInstance.current.getHtmlUnleaned()),
        );
      }
      if (resumeAutomation) {
        resumeAutomation();
      }
      if (activity) {
        setLoading(true);
        dispatch(
          caseActions.addCaseSendEmail(
            caseId,
            updatedInputs,
            attachments,
            activity.id,
          ),
        );
      } else {
        setLoading(true);
        dispatch(
          caseActions.addCaseSendEmail(caseId, updatedInputs, attachments),
        );
      }
    }
  };

  const sendLetter = (e) => {
    e.preventDefault();
    if (!inputs.to) {
      setErrorMessage("Please fill in the website owner contact info.");
      setErrorSnackbar(true);
      return;
    }
    if (
      !opponentContactDetails ||
      !(opponentContactDetails.last_name || opponentContactDetails.company) ||
      !opponentContactDetails.country ||
      !opponentContactDetails.postal_code ||
      !opponentContactDetails.city ||
      !opponentContactDetails.address_line_1
    ) {
      setErrorSnackbarTitle("Sending mail failed");
      setErrorMessage(
        "Please provide address details with name or company, address line 1, city, postal code and country",
      );
      setErrorSnackbar(true);
      return;
    }
    if (
      data.template_id &&
      !missingPlaceholder &&
      inputs.to &&
      inputs.subject &&
      inputs.content
    ) {
      setLoading(true);
      setOpenLetterCostPopup(false);
      dispatch(
        caseActions.sendCaseLetter(
          caseId,
          inputs,
          data.template_id,
          data.oldFailedLetterId,
        ),
      );
    }
  };

  const previewLetter = () => {
    if (!inputs.to) {
      setErrorSnackbarTitle("Preview failed");
      setErrorMessage("Please fill in the website owner contact info.");
      setErrorSnackbar(true);
      return;
    }
    if (
      !opponentContactDetails ||
      !(opponentContactDetails.last_name || opponentContactDetails.company) ||
      !opponentContactDetails.country ||
      !opponentContactDetails.postal_code ||
      !opponentContactDetails.city ||
      !opponentContactDetails.address_line_1
    ) {
      setErrorSnackbarTitle("Preview failed");
      setErrorMessage(
        "Please provide address details with name or company, address line 1, city, postal code and country",
      );
      setErrorSnackbar(true);
      return;
    }
    if (
      data.template_id &&
      !missingPlaceholder &&
      inputs.subject &&
      inputs.content
    ) {
      setLoading(true);
      dispatch(caseActions.previewCaseLetter(caseId, inputs, data.template_id));
    }
  };

  const handleOpenLetterCostPopup = () => {
    setOpenLetterCostPopup(true);
  };

  const handleCloseLetterCostPopup = () => {
    setOpenLetterCostPopup(false);
    dispatch(caseActions.emptyCaseLetterCost());
  };
  const getLetterCost = () => {
    setLoading(true);
    dispatch(caseActions.getCaseLetterCost(caseId, inputs, data.template_id));
  };

  useEffect(() => {
    if (caseReducer.status.CASE_GET_LETTER_COST === "success") {
      handleOpenLetterCostPopup();
      dispatch(caseActions.clearStateMessage("CASE_GET_LETTER_COST"));
      setLoading(false);
    } else if (catchError(caseReducer.status.CASE_GET_LETTER_COST)) {
      setErrorMessage(caseReducer.message);
      setErrorSnackbar(true);
      dispatch(caseActions.clearStateMessage("CASE_GET_LETTER_COST"));
      setLoading(false);
    }
  }, [dispatch, caseReducer.status.CASE_GET_LETTER_COST, caseReducer.message]);

  const handleCopyEmail = () => {
    if (doSaveDeadline === true) {
      setShowSaveReminderPopup(true);
    } else if (deleteReminder) {
      dispatch(caseActions.updateReminder(caseId));
      setDeleteReminder(false);
      handleOpenPopup();
    } else {
      setOpen(true);
    }
    setTimeout(() => dispatch(casesActions.getCases()), 500);
  };

  useEffect(() => {
    setDidPreviewLetter(false);
    setInputs((inputs) => ({
      ...inputs,
      content: editedText,
    }));
  }, [editedText]);

  // display template POST success & error — only the popup that is open should handle this
  useEffect(() => {
    /* prevents the activities loop. Without it, every closed RenderPopup on the page reacts 
    to the global CASE_SEND_EMAIL success status and dispatches getCaseActivities */
    if (!props.open) return;

    if (
      caseReducer.status.CASE_SEND_EMAIL === "success" ||
      caseReducer.status.CASE_SEND_LETTER === "success"
    ) {
      setLoading(false);
      isClosingRef.current = true;
      /* Reset so the initialization effect re-runs when the popup reopens 
      (prevents the "editor empty on reopen" bug)*/
      setEditorReady(false);
      closePopup();
      showSnackbarSuccess(
        caseReducer.status.CASE_SEND_EMAIL === "success"
          ? "Email was sent!"
          : "Letter was sent!",
        { autoHideDuration },
      );
      const handler = setTimeout(() => {
        if (caseReducer.status.CASE_SEND_LETTER === "success") {
          dispatch(customerActions.getCredits());
          handleCloseLetterCostPopup();
        }
        dispatch(caseActivityActions.getCaseActivities(caseId));
        dispatch(caseActivityActions.restEmailLoaded());
        dispatch(
          caseActions.clearStateMessage(
            caseReducer.status.CASE_SEND_EMAIL === "success"
              ? "CASE_SEND_EMAIL"
              : "CASE_SEND_LETTER",
          ),
        );
        isClosingRef.current = false;
      }, 200);
      return () => {
        clearTimeout(handler);
        isClosingRef.current = false;
      };
    } else if (
      catchError(caseReducer.status.CASE_SEND_EMAIL) ||
      catchError(caseReducer.status.CASE_SEND_LETTER)
    ) {
      isClosingRef.current = false;
      setErrorSnackbarTitle("Sending mail failed");
      setErrorMessage(caseReducer.message);
      setErrorSnackbar(true);
      setLoading(false);
      dispatch(
        caseActions.clearStateMessage(
          catchError(caseReducer.status.CASE_SEND_EMAIL)
            ? "CASE_SEND_EMAIL"
            : "CASE_SEND_LETTER",
        ),
      );
    }
    // eslint-disable-next-line
  }, [
    dispatch,
    caseReducer.status.CASE_SEND_EMAIL,
    caseReducer.status.CASE_SEND_LETTER,
    showSnackbarSuccess,
  ]);

  // display email log  POST success & error — only the open popup should handle this
  useEffect(() => {
    /* prevents the activities loop. Without it, every closed RenderPopup 
     on the page reacts to the global CASE_SEND_EMAIL success status and dispatches getCaseActivities */
    if (!props.open) return;

    const responseStatus = caseReducer.status.CASE_SAVE_EMAIL_TO_LOG;
    if (responseStatus && responseStatus === "success") {
      showSnackbarSuccess("Email was saved!", { autoHideDuration });
      setLoading(false);

      setTimeout(() => {
        /* Reset so the initialization effect re-runs when the popup reopens 
        (prevents the "editor empty on reopen" bug)*/
        setEditorReady(false);
        closePopup();
        dispatch(caseActions.clearStateMessage("CASE_SAVE_EMAIL_TO_LOG"));
        setTimeout(() => {
          setTextToContent(data.content);
          dispatch(caseActivityActions.getCaseActivities(caseId));
          dispatch(caseActivityActions.restEmailLoaded());
        }, 300);
      }, 400);
    }
    if (catchError(responseStatus)) {
      dispatch(caseActions.clearStateMessage("CASE_SAVE_EMAIL_TO_LOG"));
      setErrorMessage(caseReducer.message);
      setErrorSnackbar(true);
      setLoading(false);
    } else return;
    // eslint-disable-next-line
  }, [
    dispatch,
    caseReducer.status.CASE_SAVE_EMAIL_TO_LOG,
    caseReducer.message,
    data.content,
    caseId,
    closePopup,
    showSnackbarSuccess,
  ]);

  const handleDialogClose = () => {
    setAttachments([]);
    resetSnackbar();
    setErrorSnackbar(false);
    dispatch(caseActions.clearStateMessage("CASE_SEND_EMAIL"));
    setEditorReady(false);
    props.close();
  };

  useEffect(() => {
    if (caseReducer.status.CASE_PREVIEW_LETTER === "success") {
      showSnackbarSuccess("Letter was generated!", { autoHideDuration });
      setLoading(false);
      setDidPreviewLetter(true);
      setHasUnreviewedChanges(false);
      dispatch(caseActions.clearStateMessage("CASE_PREVIEW_LETTER"));
    }
    if (catchError(caseReducer.status.CASE_PREVIEW_LETTER)) {
      dispatch(caseActions.clearStateMessage("CASE_PREVIEW_LETTER"));
      setErrorSnackbarTitle("Preview failed");
      setErrorMessage(caseReducer.message);
      setErrorSnackbar(true);
      setLoading(false);
    } else return;
  }, [
    dispatch,
    caseReducer.status.CASE_PREVIEW_LETTER,
    caseReducer.message,
    showSnackbarSuccess,
  ]);

  const handleSaveEmailToLog = () => {
    if (!missingPlaceholder && inputs.to && inputs.subject && inputs.content) {
      dispatch(caseActions.addEmailToLog(caseId, inputs, activity?.id));
      if (resumeAutomation) {
        resumeAutomation();
      }
    } else {
      setErrorMessage(
        "Please fill missing information (content, subject,recipient email and placeholders (if needed)",
      );
      setErrorSnackbar(true);
      setOpen(false);
    }
  };

  useEffect(() => {
    if (caseReducer.status.CASE_SAVE_EMAIL_TO_LOG === "success") {
      setOpen(false);
    }
  }, [caseReducer.status.CASE_SAVE_EMAIL_TO_LOG]);

  const addAttachment = () => {
    // Create input Element
    const link = document.createElement("input");
    link.type = "file";
    link.onchange = (e) => {
      e.preventDefault();
      const file = e.target.files[0];
      const reader = new window.FileReader();
      reader.readAsArrayBuffer(file);

      /* 
          lastModified:1539873569000
          lastModifiedDate:Thu Oct 18 2018 16:39:29 GMT+0200 (Mitteleuropäische Sommerzeit) {}
          name:"ip_command.pdf"
          size:89608
          type: "application/pdf"
          webkitRelativePath:""
          */
      reader.onloadend = () => {
        setAttachments((oldVal) => {
          return [
            ...oldVal,
            ...[
              {
                filename: file.name,
                content: btoa(
                  new Uint8Array(reader.result).reduce(
                    (data, byte) => data + String.fromCharCode(byte),
                    "",
                  ),
                ),
                contentType: file.type,
                encoding: "base64",
              },
            ],
          ];
        });
      };
    };

    // Append to html link element page
    document.body.appendChild(link);

    // Start download
    link.click();

    // Clean up and remove the link
    link.parentNode.removeChild(link);
  };

  /**
   * Called when checkbox of invoice chooser is changed
   *
   * @param {event} ev Event
   * @param {boolean} value Checkbox checked?
   */
  const handleInvoiceCheckboxChange = (ev, value) => {
    let newInvoices = [...choosenInvoices];

    if (value) {
      newInvoices.push(ev.target.value);
    } else {
      newInvoices.splice(newInvoices.indexOf(ev.target.value), 1);
    }

    setChoosenInvoices(newInvoices);
  };

  /**
   * Reads a blob and converts it
   * to base64 representation
   *
   * @param {Blob} blob Blob to convert
   * @returns {string} base64 of the blob
   */
  const blobToBase64 = (blob) => {
    const reader = new FileReader();
    reader.readAsArrayBuffer(blob);
    return new Promise((resolve) => {
      reader.onloadend = () => {
        resolve(
          btoa(
            new Uint8Array(reader.result).reduce(
              (data, byte) => data + String.fromCharCode(byte),
              "",
            ),
          ),
        );
      };
    });
  };

  /**
   * Depending on the numger of invoices in the case
   * this method opens a popup with info that no invoice is there (count 0)
   * or opens a popup where to choose invoiced (chooseInvoicePopup)
   */
  const addInvoiceToMail = () => {
    let amount = 0;
    // get the number of available invoices
    if (
      caseActivityReducer.activities &&
      caseActivityReducer.activities.length > 0
    ) {
      let activities = caseActivityReducer.activities.filter(
        (x) => x.type === caseConstants.CASEACTIVITY_TYPE_INVOICE_GENERATED,
      );
      if (activities && activities.length > 0) {
        amount = activities.length;
      }
    }

    // 0 = popup "there is no invoice"
    // >= 1 = popup for choosing
    if (amount === 0) {
      setNoInvoicesPopupOpen(true);
    } else {
      setChooseInvoicePopupOpen(true);
    }
  };

  /**
   * Render helper: Creates the list for the
   * choose invoice popup
   *
   * @returns jsx
   */
  const generateInvoiceList = () => {
    var elements = [];

    let activities = caseActivityReducer.activities.filter(
      (x) => x.type === caseConstants.CASEACTIVITY_TYPE_INVOICE_GENERATED,
    );

    //sort by creation date
    activities.sort();

    //set initial value when count of invoices is 1 then
    //preselecting that invoice
    if (
      activities.length === 1 &&
      chooseInvoicePopupOpen === false &&
      choosenInvoices.length === 0
    ) {
      setChoosenInvoices([activities[0].content.invoice_id]);
    }

    // add controls for every invoice
    for (var i = 0; i < activities.length; i++) {
      elements.push(
        <>
          <tr>
            <td>
              <FormControlLabel
                value={activities[i].content.invoice_id}
                control={<Checkbox defaultChecked={activities.length === 1} />}
                onChange={(e, index) => handleInvoiceCheckboxChange(e, index)}
                label={activities[i].content.invoice_number}
              />
            </td>
            <td>
              {activities[i].content.type ===
              invoiceTemplateConstants.INVOICE_TYPE_ACTIVE
                ? "Invoice"
                : activities[i].content.type ===
                    invoiceTemplateConstants.INVOICE_TYPE_CANCELLATION
                  ? "Cancellation Invoice"
                  : activities[i].content.type ===
                      invoiceTemplateConstants.INVOICE_TYPE_CANCELLED
                    ? "Invoice (Cancelled)"
                    : "Unknown Type"}
            </td>
            <td>{formatDatetime(activities[i].created, "medium")}</td>
          </tr>
        </>,
      );
    }

    return (
      <RadioGroup name="InvoiceRadioGroup">
        {" "}
        <table cellSpacing={10}>
          <tbody>{elements}</tbody>
        </table>
      </RadioGroup>
    );
  };

  const closeChooseInvoicePopupOpen = () => {
    setChoosenInvoices([]);
    setChooseInvoicePopupOpen(false);
  };

  const addChoosenInvoicesToMail = () => {
    choosenInvoices.forEach((element) => {
      let activity = caseActivityReducer.activities.filter((x) =>
        x.content
          ? x.content.invoice_id
            ? x.content.invoice_id === element
            : false
          : false,
      )[0];
      dowloadAndAddInvoice(activity);
    });
    closeChooseInvoicePopupOpen();
  };

  return (
    <>
      <AppSnackbar {...snackbarProps} />
      <DialogPopup
        title={
          <span className={styles.title}>
            <EmailRoundedIcon className={styles.title_icon} />
            {title ||
              ` ${
                data.name === emailConstants.NEW_MAIL
                  ? "New mail"
                  : "Mail preview: " + data.name
              }`}
          </span>
        }
        open={props.open}
        onClose={(e, reason) => {
          if (reason === "backdropClick") return;
          setAttachments([]);
          props.close();
        }}
        size={"large"}
        disableEscapeKeyDown={true}
      >
        {errorSnackbar && (
          <ErrorMessagePopup
            popupTitle={errorSnackbarTitle || "Sending mail failed"}
            open={errorSnackbar}
            close={() => setErrorSnackbar(false)}
            errorMessage={errorMessage}
          />
        )}
        <div className={styles.send_email_title}>
          <AddressComponent
            emailTo={inputs.to}
            emailCc={inputs.cc}
            handleEmailToChanged={(s) =>
              handleInputChange({ target: { name: "to", value: s } })
            }
            handleSubjectChanged={(s) =>
              handleInputChange({ target: { name: "subject", value: s } })
            }
            handleEmailBccChanged={(s) =>
              handleInputChange({ target: { name: "bcc", value: s } })
            }
            handleEmailCcChanged={(s) =>
              handleInputChange({ target: { name: "cc", value: s } })
            }
            isReplyMode={isReplyMode}
            sendEmailTag={sendEmailTag}
            subject={inputs.subject}
          />
        </div>
        <div
          className={
            missingPlaceholder
              ? styles.placeholder_missing
              : styles.placeholder_missing_none
          }
        >
          <ErrorIcon className={styles.placeholder_message_icon} />
          <p className={styles.placeholder_message}>
            The marked elements do not have a value. Please add the missing
            information.
          </p>
        </div>
        <div
          style={{ maxHeight: "58vh", overflowY: "auto", overflowX: "hidden" }}
          ref={contentRef}
        >
          <Scrollbar maxHeight="57vh" options={{ suppressScrollX: true }}>
            <RichTextEditor
              addAttachmentCallback={addAttachment}
              setAttachmentsCallback={(newAttachment) =>
                setAttachments((oldVal) => [...oldVal, newAttachment])
              }
              addInvoiceCallback={addInvoiceToMail}
              setInvoiceCallback={(newInvoiceAttachement) =>
                setAttachments((oldVal) => [...oldVal, newInvoiceAttachement])
              }
              setMissingPlaceholderCallback={setMissingPlaceholder}
              setContentCallback={setContent}
              reference={editorInstance}
              onEditorReady={() => setEditorReady(true)}
              onContentChange={() => {
                setHasUnreviewedChanges(true);
              }}
            />
          </Scrollbar>
        </div>
        {attachments && attachments.length > 0 && (
          <div className={styles.attachments_row}>
            <div className={styles.attachment_prefix}>Attachments: </div>
            {attachments.map((x, index) => {
              return (
                <div key={index} className={styles.attachment_item}>
                  {x.filename}
                  {
                    <SvgIcon
                      className={styles.attachment_delete_icon}
                      onClick={(e) =>
                        setAttachments(attachments.filter((y) => y !== x))
                      }
                      children={
                        <>
                          <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
                        </>
                      }
                    ></SvgIcon>
                  }
                </div>
              );
            })}
          </div>
        )}
        <div className="action_container">
          {caseReducer.case[caseId].state !==
            caseConstants.CASE_STATE_STANDBY &&
            !resumeAutomation && (
              <div className={styles.reminder_container}>
                <Checkbox
                  className={styles.reminder_container_content}
                  checked={doSaveDeadline}
                  onChange={setReminderChecked} // todo seems wrong
                ></Checkbox>
                <div
                  style={{ marginTop: 12 }}
                  className={styles.reminder_container_content}
                >
                  set reminder
                </div>

                <Button
                  style={{ marginTop: 5, marginLeft: 12 }}
                  variant="outlined"
                  className={styles.reminder_container_content}
                  onClick={handleOpenReminderMenu}
                  endIcon={<ArrowDropDownIcon />}
                >
                  {
                    <div className={styles.menu_item}>
                      {" "}
                      <p className={styles.menu_item_span}>{reminderSetting}</p>
                    </div>
                  }
                </Button>
                <Menu
                  id="simple-menu"
                  anchorEl={reminderMenuOpen}
                  keepMounted={false}
                  open={Boolean(reminderMenuOpen)}
                  onClose={handleCloseReminderMenu}
                >
                  <MenuItem
                    className={styles.email_setup_dropdown}
                    onClick={() => handleChangeReminderOption(3)}
                  >
                    <div className={styles.menu_item}>
                      <p>in 3 days</p>{" "}
                    </div>
                  </MenuItem>
                  <MenuItem
                    className={styles.email_setup_dropdown}
                    onClick={() => handleChangeReminderOption(5)}
                  >
                    <div className={styles.menu_item}>
                      <p>in 5 days</p>{" "}
                    </div>
                  </MenuItem>
                  <MenuItem
                    className={styles.email_setup_dropdown}
                    onClick={() => handleChangeReminderOption(7)}
                  >
                    <div className={styles.menu_item}>
                      <p>in 7 days</p>{" "}
                    </div>
                  </MenuItem>
                  <MenuItem
                    className={styles.email_setup_dropdown}
                    onClick={() => handleChangeReminderOption(14)}
                  >
                    <div className={styles.menu_item}>
                      <p>in 14 days</p>{" "}
                    </div>
                  </MenuItem>
                  <MenuItem
                    className={styles.email_setup_dropdown}
                    onClick={() => handleChangeReminderOption(30)}
                  >
                    <div className={styles.menu_item}>
                      <p>in 30 days</p>{" "}
                    </div>
                  </MenuItem>
                  <MenuItem
                    className={styles.email_setup_dropdown}
                    onClick={() => handleChangeReminderOption("custom")}
                  >
                    <div className={styles.menu_item}>
                      <p>custom</p>{" "}
                    </div>
                  </MenuItem>
                </Menu>
                {showDatePicker && (
                  <DateInput
                    style={{
                      width: 150,
                      display: "inline-block",
                      float: "left",
                      marginLeft: 12,
                      marginTop: 5,
                    }}
                    label="Date"
                    id="reminder_date"
                    name="reminder_date"
                    value={reminderDate}
                    onChange={(date) => setReminderDate(date)}
                    minDate={reminderMinDate}
                  />
                )}
                <div
                  style={{ marginTop: 12, marginLeft: 12, marginRight: 12 }}
                  className={styles.reminder_container_content}
                ></div>
                <TextfieldInputSmall
                  style={{
                    width: 180,
                    display: "inline-block",
                    float: "left",
                    marginTop: "5px",
                  }}
                  label="Name"
                  id="reminder_title"
                  name="reminder_title"
                  type="string"
                  value={reminderName}
                  onChange={(event) => setReminderName(event.target.value)}
                />
              </div>
            )}
          <div className={styles.btn_container}>
            <Button className="btn_cancel" onClick={handleDialogClose}>
              Close
            </Button>
            {!loading && data?.type === emailConstants.TEMPLATE_TYPE_LETTER && (
              <Button
                className="btn_confirm"
                onClick={previewLetter}
                disabled={loading}
              >
                Preview
              </Button>
            )}
            {loading && (
              <div className={styles.loading}>
                <CircularProgress />
              </div>
            )}

            {!loading &&
              sendEmailTag &&
              caseReducer.case[caseId].state !==
                caseConstants.CASE_STATE_STANDBY && (
                <Tooltip
                  title={
                    data?.type === emailConstants.TEMPLATE_TYPE_LETTER &&
                    !didPreviewLetter
                      ? "Please preview the letter before sending it."
                      : ""
                  }
                  placement="top"
                >
                  <span>
                    <Button
                      className={`btn_confirm ${
                        missingPlaceholder ||
                        loading ||
                        !inputs.to ||
                        !inputs.subject ||
                        (data?.type === emailConstants.TEMPLATE_TYPE_LETTER &&
                          (!didPreviewLetter || hasUnreviewedChanges))
                          ? "btn_disabled"
                          : ""
                      }`}
                      onClick={
                        data?.type === emailConstants.TEMPLATE_TYPE_LETTER
                          ? getLetterCost
                          : sendEmail
                      }
                      disabled={
                        missingPlaceholder ||
                        loading ||
                        !inputs.to ||
                        !inputs.subject ||
                        (data?.type === emailConstants.TEMPLATE_TYPE_LETTER &&
                          (!didPreviewLetter || hasUnreviewedChanges))
                      }
                    >
                      send
                    </Button>
                  </span>
                </Tooltip>
              )}
          </div>
          {!sendEmailTag && (
            <Button
              className={
                missingPlaceholder
                  ? `${styles.btn_disabled}`
                  : ` btn_confirm ${styles.btn_confirm}`
              }
              onClick={handleCopyEmail}
              disabled={missingPlaceholder}
            >
              Copy to Clipboard
            </Button>
          )}
        </div>
        <AreYouSurePopup
          open={open}
          title="Content Copied!"
          close={() => handleClosePopup()}
          onClick={() => handleSaveEmailToLog()}
          confirmBtn="Ok"
          cancelBtn="Cancel"
        >
          Copied email will be saved in the Case Activities
        </AreYouSurePopup>
        <AreYouSurePopup
          open={showSaveReminderPopup}
          title="Content Copied!"
          close={() => {
            setShowSaveReminderPopup(false);
            handleOpenPopup();
          }}
          onClick={() => saveDeadline(true)}
        >
          Would you like activate the Reminder now?
        </AreYouSurePopup>
        <AreYouSurePopup
          open={showAreYouSurePopup}
          close={handleAreYouSurePopupClose}
          onClick={handleDeleteReminder}
        >
          You currently have a reminder set: Would you like to delete it?
        </AreYouSurePopup>
        <InfoPopup
          open={noInvoicesPopupOpen}
          close={() => setNoInvoicesPopupOpen(false)}
          title={"Information"}
          onClick={() => setNoInvoicesPopupOpen(false)}
          confirmBtn="Ok"
          show
        >
          <div>
            No invoices found for case {caseReducer.case[caseId].case_id}.
            <br />
            <br />
            You need to create an invoice first before you can add it as an
            attachment.
          </div>
        </InfoPopup>
        <AreYouSurePopup
          open={chooseInvoicePopupOpen}
          close={() => closeChooseInvoicePopupOpen(false)}
          title={"Choose Invoices to attach"}
          onClick={() => addChoosenInvoicesToMail()}
          confirmBtn="Ok"
          cancelBtn="Cancel"
        >
          {generateInvoiceList()}
        </AreYouSurePopup>
        <AreYouSurePopup
          open={openLetterCostPopup}
          close={() => handleCloseLetterCostPopup()}
          title={
            caseReducer.letter_cost > customerReducer.credits
              ? "Insufficient Credits"
              : "Confirm Sending"
          }
          onClick={
            caseReducer.letter_cost <= customerReducer.credits ? sendLetter : ""
          }
          confirmBtn="Send"
          confirmDisabled={caseReducer.letter_cost > customerReducer.credits}
          cancelBtn="Cancel"
        >
          {caseReducer.letter_cost > customerReducer.credits ? (
            <>
              <p>
                Your balance of <b>{customerReducer.credits} credits</b> is
                insufficient for sending this letter (
                <b>{caseReducer.letter_cost} credits</b>).
              </p>
              <p>
                Please go to <i>Admin &gt; Account &gt; Credits</i> to purchase
                additional credits.
              </p>
            </>
          ) : (
            <>
              {" "}
              <p>
                Sending this letter will cost{" "}
                <b> {caseReducer.letter_cost} credits</b>.<br />
                After sending this letter you will have{" "}
                <b>
                  {customerReducer.credits - caseReducer.letter_cost} credits
                </b>{" "}
                left in your balance.
              </p>
              <p>Would you like to send it now? </p>{" "}
            </>
          )}
        </AreYouSurePopup>
      </DialogPopup>
    </>
  );

  function dowloadAndAddInvoice(activity) {
    invoiceTemplateService
      .downloadInvoice(
        activity.case_id,
        activity.content.invoice_id,
        activity.content.invoice_number,
      )
      .then((data) => {
        data
          .blob()
          .then((blob) => {
            let b = new Blob([blob], { type: "application/pdf" });
            blobToBase64(b)
              .then((b64) => {
                setAttachments((oldVal) => {
                  return [
                    ...oldVal,
                    ...[
                      {
                        filename: activity.content.invoice_number + ".pdf",
                        content: b64,
                        contentType: "application/pdf",
                        encoding: "base64",
                      },
                    ],
                  ];
                });
              })
              .catch((e) => {
                setErrorMessage("Failed to downlaod Invoice for attachment. ");
                setErrorSnackbar(true);
              });
          })
          .catch((e) => {
            setErrorMessage("Failed to downlaod Invoice for attachment. ");
            setErrorSnackbar(true);
          });
      })
      .catch((err) => {
        setErrorMessage("Failed to downlaod Invoice for attachment. ");
        setErrorSnackbar(true);
      });
  }
}
export default RenderPopup;
